import { NavigationGuardNext, Route } from 'vue-router'
import { RouteNames } from '@/router/routes'
import store from '@/store'
import { Getters } from '@/store/getters'
import ability from '@/plugins/ability'
import { QuestionnaireApiService } from '@/data/services'
import { QuestionnaireType } from '@/data/models/Questionnaire'

/**
 * Helper function to check if organisation has a specific module enabled
 */
function hasOrgModuleAccess(moduleName: string): boolean {
  try {
    const user = store.getters[Getters.AUTH_USER]
    if (!user?.organisation_settings) return false
    return user.organisation_settings[moduleName] || false
  } catch (error) {
    return false
  }
}

function hasKey(obj: any, key: string): boolean {
  return obj != null && Object.prototype.hasOwnProperty.call(obj, key)
}

/**
 * Resolve an ability via coach-type permissions. Team-level coach type wins over the
 * coach's global coach type. Returns null when neither layer has an opinion, so the
 * caller can fall back to CASL.
 */
function checkAbilityViaCoachType(action: string): boolean | null {
  try {
    const user: any = store.getters[Getters.AUTH_USER]
    if (!user?.coach) return null

    const coachTypes: any[] = user.coach_types || []
    const globalPerms = user.coach.coach_type?.permissions
    const teams = Array.isArray(user.coach.teams) ? user.coach.teams : []

    const resolveForTeam = (team: any): boolean | null => {
      if (team.coach_type_id) {
        const teamType = coachTypes.find((ct: any) => ct.id === team.coach_type_id)
        if (teamType && hasKey(teamType.permissions, action)) {
          return teamType.permissions[action] === true
        }
      }
      if (globalPerms && hasKey(globalPerms, action)) return globalPerms[action] === true
      return null
    }

    if (teams.length === 0) {
      if (globalPerms && hasKey(globalPerms, action)) return globalPerms[action] === true
      return null
    }

    let anyAllowed = false
    let anyDecisive = false
    for (const team of teams) {
      const r = resolveForTeam(team)
      if (r === true) anyAllowed = true
      if (r !== null) anyDecisive = true
    }
    if (anyAllowed) return true
    if (anyDecisive) return false
    return null
  } catch (error) {
    return null
  }
}

async function canViewWellnessForCurrentTeam(): Promise<boolean> {
  const user: any = store.getters[Getters.AUTH_USER]
  const currentTeam: any = store.getters[Getters.APP_CURRENT_TEAM]
  const teamId = currentTeam?.id
  const coachId = user?.coach?.id

  if (!teamId || !coachId) return true

  try {
    const response = await QuestionnaireApiService.find(QuestionnaireType.WELLNESS)
    const permissions = response.data?.coach_permissions || []
    const permission = permissions.find((cp: any) => cp.team_id === teamId && cp.coach_id === coachId)
    return permission ? permission.can_view === true : true
  } catch (error) {
    return false
  }
}

/**
 * Route Guard Configuration
 * Define the abilities/permissions required for each route
 */
export interface RouteGuardConfig {
  // The route name this guard applies to
  routeName: string
  // Array of abilities required (user must have at least one)
  anyAbilities?: Array<{ action: string; subject: string | null }>
  // Array of abilities required (user must have all)
  allAbilities?: Array<{ action: string; subject: string | null }>
  // Custom guard function for complex logic
  // Return true to allow, false to deny. Guard function is responsible for calling next()
  customGuard?: (to: Route, from: Route, next: NavigationGuardNext) => Promise<boolean | void>
}

/**
 * Define your route guards here
 * Each route can have:
 * - anyAbilities: User needs at least ONE of these abilities
 * - allAbilities: User needs ALL of these abilities
 * - customGuard: Custom function for complex logic
 */
export const routeGuards: RouteGuardConfig[] = [
  {
    routeName: RouteNames.ADMIN_PLAYERS,
    anyAbilities: [
      { action: 'manage-organisation', subject: null },
      { action: 'manage-team', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_TEAMS,
    anyAbilities: [
      { action: 'manage-organisation', subject: null },
      { action: 'manage-team-players', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_STAFF,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_LOCATIONS,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_DEVICES,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_MEMBERSHIP_PLANS,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_PARENTS,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_POLAR_TEAM,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_GPEXE,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_FIELDWIZ,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_CATAPULT,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_VALD,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_KINEXON,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_LIBRE,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_PLAYER_FORM,
    anyAbilities: [
      { action: 'manage-organisation', subject: null },
      { action: 'manage-team', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_PLAYER_EDIT,
    anyAbilities: [
      { action: 'manage-organisation', subject: null },
      { action: 'manage-team', subject: null }
    ]
  },
  {
    routeName: RouteNames.ADMIN_BILLING,
    anyAbilities: [
      { action: 'manage-organisation', subject: null }
    ]
  },

  {
    routeName: RouteNames.PERFORMANCE_EVENT,
    anyAbilities: [
      { action: 'view-performance', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_gps_module') && !hasOrgModuleAccess('has_heart_rate_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.PERFORMANCE_EVENT_PLAYER,
    anyAbilities: [
      { action: 'view-performance', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_gps_module') && !hasOrgModuleAccess('has_heart_rate_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.PERFORMANCE_ADVANCED,
    anyAbilities: [
      { action: 'view-performance', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_gps_module') && !hasOrgModuleAccess('has_heart_rate_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.PERFORMANCE_PERIOD_SUMMARY,
    anyAbilities: [
      { action: 'view-performance', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_gps_module') && !hasOrgModuleAccess('has_heart_rate_module')) return false
      next()
    }
  },

  {
    routeName: RouteNames.DIAGNOSTICS,
    anyAbilities: [
      { action: 'view-diagnostics', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_diagnostics_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.DIAGNOSTIC_PROTOCOLS,
    anyAbilities: [
      { action: 'view-diagnostics', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_diagnostics_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.DIAGNOSTIC_COMPARISON,
    anyAbilities: [
      { action: 'view-diagnostics', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_diagnostics_module')) return false
      next()
    }
  },

  {
    routeName: RouteNames.MEDICAL,
    anyAbilities: [
      { action: 'view-medical', subject: null },
      { action: 'view-basic-medical', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_medical_module')) return false
      next()
    }
  },

  {
    routeName: RouteNames.TRAINING_PROTOCOLS,
    anyAbilities: [
      { action: 'view-training-protocols', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_training_builder_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.TRAINING_EXERCISES,
    anyAbilities: [
      { action: 'view-training-protocols', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_training_builder_module')) return false
      next()
    }
  },

  {
    routeName: RouteNames.DRILL_BUILDER,
    customGuard: async (to, from, next) => {
      const user = store.getters[Getters.AUTH_USER]
      const currentTeam = store.getters[Getters.APP_CURRENT_TEAM] || user?.current_team
      const teamName = currentTeam?.sport?.name
      const drillSports = ['Football', 'Futsal', 'Handball', 'Basketball', 'Hockey', 'US Football', 'Water Polo', 'Volleyball', 'Badminton', 'Baseball', 'Lacrosse', 'Pickleball', 'Tennis', 'Field Hockey']
      if (!hasOrgModuleAccess('has_drill_module')) return false
      if (!teamName || !drillSports.includes(teamName)) return false
      const coachTypeResult = checkAbilityViaCoachType('view-training-protocols')
      const canViewTraining = coachTypeResult !== null ? coachTypeResult : ability.can('view-training-protocols', null)
      if (!canViewTraining) return false
      next()
    }
  },
  {
    routeName: RouteNames.TRAINING_PROGRAMS,
    customGuard: async (to, from, next) => {
      const user = store.getters[Getters.AUTH_USER]
      if (user?.organisation_settings?.has_no_players) return false
      if (!hasOrgModuleAccess('has_training_programs_module')) return false
      if (!ability.can('create-event', null)) return false
      next()
    }
  },
  {
    routeName: RouteNames.TRAINING_PROGRAM_INSTANCES,
    customGuard: async (to, from, next) => {
      const user = store.getters[Getters.AUTH_USER]
      if (user?.organisation_settings?.has_no_players) return false
      if (!hasOrgModuleAccess('has_training_programs_module')) return false
      if (!ability.can('create-event', null)) return false
      next()
    }
  },
  {
    routeName: RouteNames.EVENTS
  },
  {
    routeName: RouteNames.EVENT_STATS,
    customGuard: async (to, from, next) => {
      const user = store.getters[Getters.AUTH_USER]
      if (user?.organisation_settings?.has_no_players) return false
      if (!hasOrgModuleAccess('has_event_stats')) return false
      if (!ability.can('view-medical', null)) return false
      next()
    }
  },
  {
    routeName: RouteNames.PLAYER_MEDIA,
    anyAbilities: [
      { action: 'view-player-media', subject: null }
    ]
  },
  {
    routeName: RouteNames.PLAYER_NOTES,
    anyAbilities: [
      { action: 'view-player-notes', subject: null }
    ]
  },
  {
    routeName: RouteNames.PLAYER_ATTENDANCE,
    anyAbilities: [
      { action: 'view-event-attendance', subject: null }
    ]
  },
  {
    routeName: RouteNames.PLAYER_DIAGNOSTICS,
    anyAbilities: [
      { action: 'view-diagnostics', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_diagnostics_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.PLAYER_MEDICAL,
    anyAbilities: [
      { action: 'view-medical', subject: null },
      { action: 'view-basic-medical', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_medical_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.PLAYER_WELLNESS,
    customGuard: async (to, from, next) => {
      const user = store.getters[Getters.AUTH_USER]
      if (!hasOrgModuleAccess('has_itl_module')) return false
      if (!user?.can_view_wellness_questionnaire) return false

      const canViewTeamWellness = await canViewWellnessForCurrentTeam()
      if (!canViewTeamWellness) return false

      next()
    }
  },
  {
    routeName: RouteNames.PLAYER_OVERVIEW,
    anyAbilities: [
      { action: 'view-performance', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_itl_module') && !hasOrgModuleAccess('has_diagnostics_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.PLAYER_LOAD,
    anyAbilities: [
      { action: 'view-performance', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_itl_module') && !hasOrgModuleAccess('has_gps_module') && !hasOrgModuleAccess('has_heart_rate_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.PLAYER_EVENTS,
    anyAbilities: [
      { action: 'view-performance', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_gps_module') && !hasOrgModuleAccess('has_heart_rate_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.PLAYER_DAILY_ACTIVITY,
    anyAbilities: [
      { action: 'view-performance', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_daily_activity_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.PLAYER_SCOUTING,
    customGuard: async (to, from, next) => {
      const user = store.getters[Getters.AUTH_USER]
      if (!hasOrgModuleAccess('has_scouting_module')) return false
      if (!user?.can_view_scouting_questionnaire) return false
      next()
    }
  },
  {
    routeName: RouteNames.PLAYER_SESSIONS,
    anyAbilities: [
      { action: 'view-training-protocols', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_training_builder_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.MEMBERSHIP_FEES,
    anyAbilities: [
      { action: 'view-membership', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_membership_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.WELLNESS,
    customGuard: async (to, from, next) => {
      const user = store.getters[Getters.AUTH_USER]
      if (user?.organisation_settings?.has_no_players) return false
      if (!hasOrgModuleAccess('has_itl_module')) return false
      if (!user?.can_view_wellness_questionnaire) return false
      const canViewTeamWellness = await canViewWellnessForCurrentTeam()
      if (!canViewTeamWellness) return false
      next()
    }
  },
  {
    routeName: RouteNames.DAILY_ACTIVITY,
    customGuard: async (to, from, next) => {
      const user = store.getters[Getters.AUTH_USER]
      if (user?.organisation_settings?.has_no_players) return false
      if (!hasOrgModuleAccess('has_daily_activity_module')) return false
      if (!ability.can('view-performance', null)) return false
      next()
    }
  },
  {
    routeName: RouteNames.LOAD,
    customGuard: async (to, from, next) => {
      const user = store.getters[Getters.AUTH_USER]
      if (user?.organisation_settings?.has_no_players) return false
      if (!hasOrgModuleAccess('has_itl_module') && !hasOrgModuleAccess('has_gps_module') && !hasOrgModuleAccess('has_heart_rate_module')) return false
      if (!ability.can('view-performance', null)) return false
      next()
    }
  },
  {
    routeName: RouteNames.REPORTS
  },
  {
    routeName: RouteNames.DASHBOARD_PLAYERS
  },
  {
    routeName: RouteNames.COACH_INSIGHTS_INDIVIDUAL,
    anyAbilities: [
      { action: 'preview-coach-exercise-insights', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_training_builder_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.COACH_INSIGHTS,
    anyAbilities: [
      { action: 'preview-coach-exercise-insights', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_training_builder_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.PLAYER_INSIGHTS,
    anyAbilities: [
      { action: 'preview-player-exercise-insights', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_training_builder_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.PLAYER_INSIGHTS_INDIVIDUAL,
    anyAbilities: [
      { action: 'preview-player-exercise-insights', subject: null }
    ],
    customGuard: async (to, from, next) => {
      if (!hasOrgModuleAccess('has_training_builder_module')) return false
      next()
    }
  },
  {
    routeName: RouteNames.MEDIA_LIBRARY,
    anyAbilities: [
      { action: 'view-player-media', subject: null },
      { action: 'view-event-media', subject: null }
    ]
  },
  {
    routeName: RouteNames.SCOUTING,
    customGuard: async (to, from, next) => {
      try {
        const user = store.getters[Getters.AUTH_USER]
        if (user?.organisation_settings?.has_no_players) return false
        if (!hasOrgModuleAccess('has_scouting_module')) return false
        if (!user?.can_view_scouting_questionnaire) return false
        next()
      } catch (error) {
        return false
      }
    }
  }
]

/**
 * Check if user has required abilities for a route
 */
export function checkRouteAbilities(guard: RouteGuardConfig): boolean {
  try {
    if (guard.anyAbilities && guard.anyAbilities.length > 0) {
      const hasAnyAbility = guard.anyAbilities.some(({ action, subject }) => {
        try {
          const ctResult = checkAbilityViaCoachType(action)
          return ctResult !== null ? ctResult : ability.can(action, subject)
        } catch (error) {
          return false
        }
      })
      if (!hasAnyAbility) return false
    }

    if (guard.allAbilities && guard.allAbilities.length > 0) {
      const hasAllAbilities = guard.allAbilities.every(({ action, subject }) => {
        try {
          const ctResult = checkAbilityViaCoachType(action)
          return ctResult !== null ? ctResult : ability.can(action, subject)
        } catch (error) {
          return false
        }
      })
      if (!hasAllAbilities) return false
    }

    return true
  } catch (error) {
    return false
  }
}

/**
 * Get the guard configuration for a route
 */
export function getRouteGuard(routeName: string | null | undefined): RouteGuardConfig | undefined {
  if (!routeName) return undefined
  return routeGuards.find(guard => guard.routeName === routeName)
}

/**
 * Main navigation guard function
 * Call this in your router.beforeEach
 */
export async function navigationGuard(to: Route, from: Route, next: NavigationGuardNext): Promise<void> {
  try {
    const isAuthenticated = store.getters[Getters.IS_AUTHENTICATED]

    const fallbackRoute = to.name?.toString().startsWith('admin')
      ? RouteNames.OVERVIEW
      : RouteNames.EVENTS

    const guard = getRouteGuard(to.name)

    if (!guard) {
      next()
      return
    }

    if (!isAuthenticated) {
      next({ name: RouteNames.LOGIN })
      return
    }

    const hasAbilities = checkRouteAbilities(guard)
    if (!hasAbilities) {
      next({ name: RouteNames.OVERVIEW })
      return
    }

    if (guard.customGuard) {
      try {
        const result = await guard.customGuard(to, from, next)
        if (result === false) {
          next({ name: RouteNames.OVERVIEW })
        }
      } catch (error) {
        next({ name: RouteNames.OVERVIEW })
      }
      return
    }

    next()
  } catch (error) {
    const fallback = to.name?.toString().startsWith('admin') ? RouteNames.OVERVIEW : RouteNames.EVENTS
    next({ name: fallback })
  }
}
